import React, { useCallback, useEffect, useState } from 'react';
import { MessageBarActions, MessageBarBody, MessageBarTitle } from '@fluentui/react-components';
import { Warning16Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { GuideDrawer } from '../../../theme/components/GuideDrawer';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideTable, GuideTh, guideCellClassName as cell, guideRowClassName } from '../../../theme/components/GuideTable';
import { GuideEmptyState } from '../../../theme/components/GuideEmptyState';
import { GuideSkeleton } from '../../../theme/components/GuideSkeleton';
import { GuideBannerAction, GuideLinkButton } from '../../../theme/components/GuideButtons';
import { StatusPill } from '../../../theme/components/StatusPill';
import {
  formatDateTime,
  isSyncActive,
  syncScopeText,
  syncStatusLabelKey,
  syncStatusTone,
  syncTriggerLabelKey,
} from '../../../functions/ProviderConsumptions.Functions';
import { getApiErrorMessage } from '../../../functions/ProviderConsumptionImport.Functions';
import { ProviderSyncRun, ProviderSyncStatus } from '../../../models/Entities/ProviderConsumptions/ProviderConsumptions';

export interface ProviderSyncRunsDrawerProps {
  open: boolean;
  onDismiss: () => void;
  providerName: string;
  LoadRuns: () => Promise<ProviderSyncRun[]>;
  onOpenAlarms: (run: ProviderSyncRun) => void;
}

/** Historial de sincronizaciones del proveedor (más nueva primero). Nunca muestra el stack trace. */
export const ProviderSyncRunsDrawer = (props: ProviderSyncRunsDrawerProps) => {
  const { t } = useTranslation();
  const tr = (path: string, options?: Record<string, unknown>) => t(`providerConsumptions.runs.${path}`, options);
  const [runs, setRuns] = useState<ProviderSyncRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(undefined);

  const load = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      setRuns(await props.LoadRuns());
    } catch (failure: unknown) {
      setError(failure);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (props.open) load();
  }, [props.open, load]);

  const renderBody = () => {
    if (loading && runs.length === 0) return <GuideSkeleton rows={4} label={tr('loading')} data-testid="pcs-runs-loading" />;
    if (error !== undefined) return null;
    if (runs.length === 0) return <GuideEmptyState title={tr('emptyTitle')} text={tr('emptyText')} data-testid="pcs-runs-empty" />;
    return (
      <GuideTable ariaLabel={tr('title')} data-testid="pcs-runs-table" scrollTestId="pcs-runs-table-scroll">
        <thead>
          <tr>
            <GuideTh>{tr('columns.requestedAt')}</GuideTh>
            <GuideTh>{tr('columns.status')}</GuideTh>
            <GuideTh>{tr('columns.trigger')}</GuideTh>
            <GuideTh>{tr('columns.scope')}</GuideTh>
            <GuideTh>{tr('columns.finishedAt')}</GuideTh>
            <GuideTh align="end">{tr('columns.changes')}</GuideTh>
            <GuideTh>{tr('columns.alarms')}</GuideTh>
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => {
            const failed = run.status === ProviderSyncStatus.Fail || run.status === ProviderSyncStatus.PartiallyFailed;
            return (
              <tr key={run.id} className={guideRowClassName({ pending: failed })} data-testid={`pcs-run-${run.id}`}>
                <td className="ichtys-num pcs-nowrap">{formatDateTime(run.requestedAt)}</td>
                <td>
                  <StatusPill tone={syncStatusTone(run.status)}>{t(syncStatusLabelKey(run.status))}</StatusPill>
                  {failed && run.errorMessage && <span className="pcs-secondary">{run.errorMessage}</span>}
                </td>
                <td>{t(syncTriggerLabelKey(run.trigger))}</td>
                <td>{syncScopeText(run, t)}</td>
                <td className="ichtys-num pcs-nowrap">{isSyncActive(run) ? tr('inProgress') : formatDateTime(run.finishedAt)}</td>
                <td className={cell('end', 'num')}>
                  {tr('changesValue', { created: run.createdCount ?? 0, updated: run.updatedCount ?? 0, deleted: run.deletedCount ?? 0 })}
                </td>
                <td>
                  {(run.alarmCount ?? 0) > 0 ? (
                    <GuideLinkButton size="small" icon={<Warning16Regular />} onClick={() => props.onOpenAlarms(run)} data-testid={`pcs-run-alarms-${run.id}`}>
                      {tr('viewAlarms', { count: run.alarmCount })}
                    </GuideLinkButton>
                  ) : (
                    <span className="pcs-muted">0</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </GuideTable>
    );
  };

  return (
    <GuideDrawer
      open={props.open}
      size="large"
      onDismiss={props.onDismiss}
      eyebrow={tr('eyebrow', { name: props.providerName })}
      title={tr('title')}
      subtitle={tr('subtitle')}
      testId="pcs-runs-drawer"
    >
      {error !== undefined && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="pcs-runs-error">
          <MessageBarBody>
            <MessageBarTitle>{tr('loadError')}</MessageBarTitle>
            {getApiErrorMessage(error)}
          </MessageBarBody>
          <MessageBarActions>
            <GuideBannerAction onClick={load}>{t('providerConsumptions.errors.retry')}</GuideBannerAction>
          </MessageBarActions>
        </GuideMessageBar>
      )}
      {renderBody()}
    </GuideDrawer>
  );
};

export default ProviderSyncRunsDrawer;
